import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaBrain, FaCloud, FaDatabase, FaLanguage, FaLock, FaNetworkWired, FaStackExchange, FaGraduationCap } from 'react-icons/fa';

const Skills = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const skillCategories = [
    {
      title: 'Programming Languages',
      icon: <FaLanguage />,
      desc: 'Core languages used for systems, backend, smart contracts, and low-level work.',
      skills: ['Java', 'Python', 'C', 'C++', 'Solidity', 'SQL', 'CQL', 'Bash', 'Assembly']
    },
    {
      title: 'Frameworks & Technologies',
      icon: <FaStackExchange />,
      desc: 'Backend, frontend, testing, and service frameworks for production apps.',
      skills: [
        'Spring Boot', 'Apache Camel', 'REST APIs', 'OpenTelemetry',
        'Microservices', 'Cypress', 'JUnit', 'Mockito',
        'React', 'Node.js', 'TypeScript', 'Flask',
        'Spring MVC', 'AngularJS', 'Android Studio'
      ]
    },
    {
      title: 'Cloud & DevOps',
      icon: <FaCloud />,
      desc: 'Deployment, orchestration, messaging, and CI/CD tooling.',
      skills: ['AWS', 'Docker', 'Kubernetes', 'Kafka', 'RabbitMQ', 'Git']
    },
    {
      title: 'Databases',
      icon: <FaDatabase />,
      desc: 'Relational, graph, cache, and big-data storage systems.',
      skills: ['MongoDB', 'Neo4j', 'MySQL', 'Redis', 'Spark', 'DBMS']
    },
    {
      title: 'Security & Malware',
      icon: <FaLock />,
      desc: 'Reverse engineering, malware analysis, and network inspection.',
      skills: [
        'Wireshark', 'tcpdump', 'Kali Linux', 'Ghidra', 'IDA', 'x64dbg',
        'OllyDbg', 'dnSpy', 'Fakenet-ng', 'Process Monitor',
        'Sysinternals', 'Detect It Easy', 'VirusTotal'
      ]
    },
    {
      title: 'Blockchain',
      icon: <FaNetworkWired />,
      desc: 'Smart contract development and decentralized tooling.',
      skills: ['Ethereum', 'Truffle', 'Ganache', 'MetaMask', 'IPFS']
    },
    {
      title: 'Core Concepts',
      icon: <FaBrain />,
      desc: 'Foundational computer science and architecture principles.',
      skills: [
        'OOP', 'Design Patterns', 'Data Structures',
        'Distributed Systems', 'Cryptography',
        'Cybersecurity', 'Machine Learning',
        'Consensus Algorithms', 'Cloud Computing'
      ]
    },
    {
      title: 'Advanced Coursework',
      icon: <FaGraduationCap />,
      desc: 'Formal academic focus areas and deep technical domains.',
      skills: [
        'Algorithms', 'Ethical Hacking', 'NLP',
        'Malware Reverse Engineering',
        'System Security', 'Networking'
      ]
    }
  ];


  const container = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
  };

  const card = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: .6 }
    }
  };

  const chip = {
    hidden: { opacity: 0, scale: .8 },
    visible: { opacity: 1, scale: 1 }
  };

  return (
    <section className="skills" id="skills" ref={ref}>
      <div className="container">

        {/* header unchanged */}
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
        >
          <span className="section-tag">
            &lt;skills /&gt;
          </span>

          <h2 className="section-title">
            <span className="keyword">Technical</span> Skills
            <span className="cursor-blink">.</span>
          </h2>

          <p className="section-description">
            Tools, technologies, and domains I actively work in
          </p>
        </motion.div>

        {/* Tech Stack Belt */}
        <div className="tech-belt-container">
          <div className="tech-belt">
            <div className="tech-belt-track">
              {/* First set */}
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" alt="Java" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" alt="Python" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg" alt="Spring" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" alt="Docker" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg" alt="Kubernetes" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" alt="AWS" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" alt="MongoDB" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" alt="MySQL" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" alt="Node.js" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" alt="TypeScript" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg" alt="C" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg" alt="C++" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg" alt="Redis" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg" alt="Linux" /></div>
              
              {/* Duplicate set for seamless loop */}
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" alt="Java" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" alt="Python" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" alt="React" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg" alt="Spring" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" alt="Docker" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg" alt="Kubernetes" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" alt="AWS" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" alt="MongoDB" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" alt="MySQL" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" alt="Node.js" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" alt="TypeScript" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg" alt="C" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg" alt="C++" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg" alt="Redis" /></div>
              <div className="tech-logo"><img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg" alt="Linux" /></div>
            </div>
          </div>
        </div>

        {/* modern grid */}
        <motion.div
          className="skills-modern-grid"
          variants={container}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {skillCategories.map((cat, i) => (
            <motion.div
              key={cat.title}
              className="skills-card-modern"
              variants={card}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="skills-card-header">
                <span className="skills-card-icon">{cat.icon}</span>
                <h3>{cat.title}</h3>
              </div>

              <p className="skills-card-desc">{cat.desc}</p>


              <motion.div
                className="skills-chip-grid"
                variants={container}
              >
                {cat.skills.map(skill => (
                  <motion.span
                    key={skill}
                    className="skills-chip"
                    variants={chip}
                    whileHover={{ scale: 1.08 }}
                  >
                    {skill}
                  </motion.span>
                ))}
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

      </div>

      {/* ===== minimal modern styles (scoped) ===== */}
      
      <style>{`

/* ===== TECH BELT ===== */

.tech-belt-container {
  margin: 48px 0;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.tech-belt-container::before,
.tech-belt-container::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 120px;
  z-index: 2;
  pointer-events: none;
}

.tech-belt-container::before {
  left: 0;
  background: linear-gradient(to right, rgba(10,10,15,1), transparent);
}

.tech-belt-container::after {
  right: 0;
  background: linear-gradient(to left, rgba(10,10,15,1), transparent);
}

.tech-belt {
  display: flex;
  width: 100%;
}

.tech-belt-track {
  display: flex;
  gap: 48px;
  animation: scrollBelt 40s linear infinite;
  will-change: transform;
}

@keyframes scrollBelt {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

.tech-logo {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  background: rgba(15,15,18,.6);
  border: 1px solid rgba(0,255,65,.15);
  border-radius: 12px;
  transition: all .3s ease;
}

.tech-logo:hover {
  transform: translateY(-4px);
  border-color: rgba(0,255,65,.4);
  box-shadow: 0 8px 24px rgba(0,255,65,.15);
}

.tech-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: brightness(0.9);
  transition: filter .3s ease;
}

.tech-logo:hover img {
  filter: brightness(1.1);
}

/* Pause animation on hover */
.tech-belt-container:hover .tech-belt-track {
  animation-play-state: paused;
}

/* ===== GRID ===== */

.skills-modern-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px,1fr));
  gap: 28px;
  margin-top: 60px;
}

/* ===== CARD — SLEEK GLASS + GLOW ===== */

.skills-card-modern {
  position: relative;
  background: rgba(15,15,18,.72);
  border: 1px solid rgba(0,255,65,.18);
  border-radius: 16px;
  padding: 22px 22px 26px;
  backdrop-filter: blur(12px);
  overflow: hidden;
  transition: all .35s ease;
}

/* animated soft gradient glow layer */
.skills-card-modern::before {
  content: "";
  position: absolute;
  inset: -1px;
  background: linear-gradient(
    120deg,
    transparent 20%,
    rgba(0,255,65,0.10),
    rgba(0,200,255,0.08),
    transparent 80%
  );
  opacity: 0.45;
  animation: skillGlow 9s linear infinite;
  z-index: 0;
}

.skills-card-modern > * {
  position: relative;
  z-index: 1;
}

@keyframes skillGlow {
  0% { transform: translateX(-45%); }
  100% { transform: translateX(45%); }
}

/* hover polish */
.skills-card-modern:hover {
  border-color: rgba(0,255,65,.45);
  box-shadow:
    0 20px 60px rgba(0,255,65,.14),
    0 0 28px rgba(0,255,65,.10);
}

/* ===== HEADER ===== */

.skills-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.skills-card-header h3 {
  font-size: 1.05rem;
  font-weight: 600;
}

.skills-card-icon {
  font-size: 1.35rem;
  opacity: .9;
}

/* ===== DESCRIPTION ===== */

.skills-card-desc {
  font-size: 0.82rem;
  line-height: 1.45;
  opacity: 0.75;
  margin-bottom: 16px;
  color: #9fb3a8;
}

/* ===== SKILL CHIPS ===== */

.skills-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.skills-chip {
  font-size: .78rem;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0,0,0,.45);
  border: 1px solid rgba(0,255,65,.28);
  color: #b7ffcf;
  letter-spacing: .3px;
  white-space: nowrap;
  transition: all .2s ease;
}

.skills-chip:hover {
  background: rgba(0,255,65,.12);
  border-color: rgba(0,255,65,.55);
  box-shadow: 0 0 12px rgba(0,255,65,.18);
}

`}</style>


    </section>
  );
};

export default Skills;